/* Cart milestones: one bar for free shipping and the free gift tiers (snippets/cart-milestones.liquid).
   Minimums arrive in the shop currency and are converted with Shopify.currency.rate, the rate checkout uses for
   discount minimums. Also adds the gift when it unlocks and removes it when the cart falls back below the minimum.
   When the gift has variants, the customer picks one (e.g. a colour); if they never add the gift, the default
   variant is added when they press checkout. */
(function () {
  'use strict';

  if (window.customElements.get('cart-milestones')) return;

  // Set once the gift has been in the cart this session. If it later disappears while still unlocked, the customer
  // took it out, so it is offered with a button instead of being forced back in (or added at checkout).
  var AUTO_ADDED_KEY = 'cancha_gift_auto_added_';
  var checkoutReady = false;
  var busy = false;

  function storage(method, key, value) {
    try {
      return window.sessionStorage[method](key, value);
    } catch (error) {
      return null;
    }
  }

  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function exchangeRate() {
    var rate = Number(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate);
    return rate > 0 ? rate : 1;
  }

  // Minimum in the customer's currency, in cents. Rounded to 3 decimals first so 250 * 1.0 stays 25000.
  function toLocalCents(base, rate) {
    return Math.ceil(Math.round(base * 100 * rate * 1000) / 1000);
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, function (character) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character];
    });
  }

  function formatMoney(cents, currency, wholeUnits) {
    var value = cents / 100;
    var locale = document.documentElement.lang || 'en';
    var options = { style: 'currency', currency: currency, currencyDisplay: 'narrowSymbol' };
    if (wholeUnits) {
      value = Math.ceil(value);
      options.minimumFractionDigits = 0;
      options.maximumFractionDigits = 0;
    }
    try {
      return new Intl.NumberFormat(locale, options).format(value);
    } catch (error) {
      return value.toFixed(wholeUnits ? 0 : 2) + ' ' + currency;
    }
  }

  function fillTemplate(template, amount, gift) {
    return escapeHtml(template || '')
      .split('[amount]').join(amount ? '<strong>' + escapeHtml(amount) + '</strong>' : '')
      .split('[gift]').join(escapeHtml(gift || ''));
  }

  async function updateCart(path, body) {
    var response = await fetch(cartRoot() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    if (!response.ok) throw new Error('Cart request failed: ' + path);
    return response.json();
  }

  function addGift(variantId) {
    return updateCart('cart/add.js', {
      items: [{ id: Number(variantId), quantity: 1, properties: { _gift: 'true' } }]
    });
  }

  function refreshCart() {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  class CartMilestones extends HTMLElement {
    connectedCallback() {
      var configElement = this.querySelector('[data-milestones-config]');
      if (!configElement) return;

      try {
        this.config = JSON.parse(configElement.textContent);
      } catch (error) {
        console.error('[Cancha milestones] Bad config', error);
        return;
      }

      this.milestones = this.buildMilestones();
      this.render();
      this.observeLabels();
      this.bindClaimButtons();
      this.syncGifts();
    }

    buildMilestones() {
      var rate = exchangeRate();
      return this.config.milestones
        .map(function (milestone) {
          var threshold = toLocalCents(milestone.base, rate);
          return Object.assign({}, milestone, {
            threshold: threshold,
            reached: milestone.subtotal >= threshold
          });
        })
        .sort(function (a, b) {
          return a.threshold - b.threshold;
        });
    }

    disconnectedCallback() {
      if (this.labelObserver) this.labelObserver.disconnect();
    }

    // Re-space the labels whenever the drawer gets a width (it is hidden on first render) or is resized.
    observeLabels() {
      var self = this;
      var labels = this.querySelector('.cart-milestones__labels');
      if (!labels) return;
      if ('ResizeObserver' in window) {
        this.labelObserver = new ResizeObserver(function () { self.layoutLabels(); });
        this.labelObserver.observe(labels);
      }
      this.layoutLabels();
    }

    // Each label sits under its marker (the last one right-aligned to the end). When two milestones are close,
    // e.g. free shipping at £101 and the gift at £114, the labels would overlap, so nudge them apart:
    // right to left so none runs into the label after it, then left to right so none spills off the start.
    layoutLabels() {
      var self = this;
      var wrapper = this.querySelector('.cart-milestones__labels');
      var width = wrapper ? wrapper.clientWidth : 0;
      if (!width || !this.milestones) return;

      var gap = 8;
      var boxes = this.milestones
        .map(function (milestone, index, all) {
          var label = self.querySelector('[data-label="' + milestone.id + '"]');
          if (!label) return null;
          var labelWidth = label.offsetWidth;
          var anchor = (parseFloat(label.style.left) / 100) * width;
          var isLast = index === all.length - 1;
          return { label: label, width: labelWidth, left: isLast ? anchor - labelWidth : anchor - labelWidth / 2 };
        })
        .filter(Boolean);

      for (var i = boxes.length - 2; i >= 0; i--) {
        boxes[i].left = Math.min(boxes[i].left, boxes[i + 1].left - gap - boxes[i].width);
      }
      for (var j = 0; j < boxes.length; j++) {
        var minLeft = j === 0 ? 0 : boxes[j - 1].left + boxes[j - 1].width + gap;
        boxes[j].left = Math.max(boxes[j].left, minLeft);
      }

      boxes.forEach(function (box) {
        box.label.style.transform = 'translateX(' + (box.left - (parseFloat(box.label.style.left) / 100) * width) + 'px)';
      });
    }

    doneText(milestone) {
      var texts = this.config.texts;
      return milestone.type === 'shipping'
        ? fillTemplate(texts.shippingDone)
        : fillTemplate(texts.giftDone, null, milestone.title);
    }

    render() {
      var self = this;
      var currency = this.config.currency;
      var texts = this.config.texts;
      var milestones = this.milestones;
      var last = milestones[milestones.length - 1];
      var max = Math.max(last.threshold, 1);

      milestones.forEach(function (milestone, index) {
        var position = (milestone.threshold / max) * 100;
        var marker = self.querySelector('[data-marker="' + milestone.id + '"]');
        var label = self.querySelector('[data-label="' + milestone.id + '"]');
        var isLast = index === milestones.length - 1;

        [marker, label].forEach(function (element) {
          if (!element) return;
          element.style.left = position + '%';
          element.classList.toggle('is-end', isLast);
          element.classList.toggle('is-reached', milestone.reached);
        });
        if (label) label.querySelector('[data-amount]').textContent = formatMoney(milestone.threshold, currency, true);
      });

      // The line follows the highest milestone's subtotal
      var fillPercent = Math.max(0, Math.min(100, (last.subtotal / max) * 100));
      this.querySelector('[data-fill]').style.width = fillPercent + '%';

      var next = milestones.find(function (milestone) { return !milestone.reached; });
      var lastReached = milestones.filter(function (milestone) { return milestone.reached; }).pop();
      var message;

      if (!next) {
        var lastGift = milestones.filter(function (milestone) { return milestone.type === 'gift'; }).pop();
        var hasShipping = milestones.some(function (milestone) { return milestone.type === 'shipping'; });
        message = hasShipping && lastGift
          ? fillTemplate(texts.allDone, null, lastGift.title)
          : this.doneText(lastReached);
      } else {
        var remaining = formatMoney(Math.max(next.threshold - next.subtotal, 0), currency, false);
        var toText = next.type === 'shipping'
          ? fillTemplate(texts.toShipping, remaining)
          : fillTemplate(texts.toGift, remaining, next.title);
        message = (lastReached ? this.doneText(lastReached) + ' ' : '') + toText;
      }

      var messageElement = this.querySelector('[data-milestones-message]');
      messageElement.innerHTML = message;

      var track = this.querySelector('[data-milestones-track]');
      var reachedCount = milestones.filter(function (milestone) { return milestone.reached; }).length;
      track.setAttribute('aria-valuenow', Math.round((reachedCount / milestones.length) * 100));
      track.setAttribute('aria-valuetext', messageElement.textContent);
    }

    // The variant the customer has picked for a gift, or the default (first available) one.
    selectedVariantId(milestone) {
      var select = this.querySelector('[data-claim="' + milestone.id + '"] [data-gift-variant]');
      return (select && select.value) || milestone.variantId;
    }

    bindClaimButtons() {
      var self = this;

      this.querySelectorAll('[data-gift-variant]').forEach(function (select) {
        select.addEventListener('change', function () {
          var claim = select.closest('[data-claim]');
          var option = select.options[select.selectedIndex];
          var image = claim.querySelector('[data-claim-image]');
          if (image && option && option.dataset.image) {
            image.removeAttribute('srcset');
            image.removeAttribute('sizes');
            image.src = option.dataset.image;
          }
          var milestone = self.milestones.find(function (item) { return item.id === claim.dataset.claim; });
          if (milestone) self.swapGift(milestone, select);
        });
      });

      this.querySelectorAll('[data-claim-button]').forEach(function (button) {
        button.addEventListener('click', async function () {
          if (busy) return;
          busy = true;
          button.disabled = true;
          button.setAttribute('aria-busy', 'true');
          var milestone = self.milestones.find(function (item) { return item.id === button.closest('[data-claim]').dataset.claim; });
          try {
            await addGift(milestone ? self.selectedVariantId(milestone) : button.dataset.variantId);
            if (milestone) storage('setItem', AUTO_ADDED_KEY + milestone.variantId, '1');
            refreshCart();
          } catch (error) {
            console.error('[Cancha milestones]', error);
            button.disabled = false;
            button.removeAttribute('aria-busy');
          } finally {
            busy = false;
          }
        });
      });
    }

    // A gift already in the cart switches to the newly picked variant: take the old line out, add the new one.
    async swapGift(milestone, select) {
      if (!milestone.lineKey || String(milestone.lineVariantId) === String(select.value)) return;
      if (busy) return;
      busy = true;
      select.disabled = true;
      try {
        var updates = {};
        updates[milestone.lineKey] = 0;
        await updateCart('cart/update.js', { updates: updates });
        await addGift(select.value);
        refreshCart();
      } catch (error) {
        console.error('[Cancha milestones]', error);
        refreshCart();
      } finally {
        busy = false;
        select.disabled = false;
      }
    }

    // Gifts that are unlocked but not in the cart, and that the customer has not taken out themselves.
    // Pressing checkout adds these (in the picked or default variant) first.
    pendingGifts() {
      var self = this;
      return (this.milestones || []).filter(function (milestone) {
        return milestone.type === 'gift' && milestone.reached && !milestone.lineKey && !storage('getItem', AUTO_ADDED_KEY + milestone.variantId);
      }).map(function (milestone) {
        return { milestone: milestone, variantId: self.selectedVariantId(milestone) };
      });
    }

    async syncGifts() {
      if (busy) return;

      var self = this;
      var updates = {};
      var toAdd = null;
      var autoAdd = this.config.autoAdd === true;

      this.milestones.forEach(function (milestone) {
        if (milestone.type !== 'gift') return;

        var autoKey = AUTO_ADDED_KEY + milestone.variantId;
        var claim = self.querySelector('[data-claim="' + milestone.id + '"]');

        (milestone.extraKeys || []).forEach(function (key) {
          if (key) updates[key] = 0;
        });

        if (milestone.lineKey && milestone.reached) storage('setItem', autoKey, '1');

        if (milestone.lineKey && !milestone.reached) {
          // Below the minimum the discount no longer covers the gift, so take it out.
          updates[milestone.lineKey] = 0;
          storage('removeItem', autoKey);
        } else if (milestone.lineKey && milestone.lineQuantity > 1) {
          updates[milestone.lineKey] = 1;
        }

        var unclaimed = milestone.reached && !milestone.lineKey;
        // Auto-add once per session. If the customer then removes it, offer it instead of forcing it back in.
        var shouldAutoAdd = unclaimed && autoAdd && !storage('getItem', autoKey) && !toAdd;

        if (shouldAutoAdd) {
          toAdd = milestone;
          storage('setItem', autoKey, '1');
        }

        if (claim) {
          var hasChoices = claim.hasAttribute('data-has-choices');
          var select = claim.querySelector('[data-gift-variant]');
          var button = claim.querySelector('[data-claim-button]');
          var claimed = milestone.reached && milestone.lineKey;
          // Show the row to add the gift, or, once it is in the cart, to change its colour
          claim.hidden = !((unclaimed && !shouldAutoAdd) || (claimed && hasChoices));
          if (button) button.hidden = !unclaimed;
          if (select && milestone.lineVariantId && select.querySelector('option[value="' + milestone.lineVariantId + '"]')) {
            select.value = String(milestone.lineVariantId);
            var option = select.options[select.selectedIndex];
            var image = claim.querySelector('[data-claim-image]');
            if (image && option && option.dataset.image) {
              image.removeAttribute('srcset');
              image.removeAttribute('sizes');
              image.src = option.dataset.image;
            }
          }
        }
      });

      if (!toAdd && Object.keys(updates).length === 0) return;

      busy = true;
      try {
        if (Object.keys(updates).length) await updateCart('cart/update.js', { updates: updates });
        if (toAdd) await addGift(toAdd.variantId);
        refreshCart();
      } catch (error) {
        console.error('[Cancha milestones]', error);
      } finally {
        busy = false;
      }
    }
  }

  window.customElements.define('cart-milestones', CartMilestones);

  // Checkout from the cart drawer: add any unlocked gift the customer never added, then carry on to checkout.
  document.addEventListener('submit', async function (event) {
    var submitter = event.submitter;
    var form = event.target;
    if (checkoutReady || !submitter || submitter.name !== 'checkout' || !form.closest('cart-drawer')) return;

    var milestones = form.closest('cart-drawer').querySelector('cart-milestones');
    var pending = milestones && milestones.pendingGifts ? milestones.pendingGifts() : [];
    if (!pending.length) return;

    event.preventDefault();
    submitter.setAttribute('aria-busy', 'true');
    try {
      await updateCart('cart/add.js', {
        items: pending.map(function (gift) {
          return { id: Number(gift.variantId), quantity: 1, properties: { _gift: 'true' } };
        })
      });
      pending.forEach(function (gift) { storage('setItem', AUTO_ADDED_KEY + gift.milestone.variantId, '1'); });
    } catch (error) {
      // Never block checkout over the gift
      console.error('[Cancha milestones]', error);
    }
    checkoutReady = true;
    submitter.removeAttribute('aria-busy');
    form.requestSubmit(submitter);
  }, true);
})();
